import React from "react";
import SEO from "../../components/common/SEO";
import {
  Database,
  Shield,
  Plug,
  Activity,
  Zap,
  CheckCircle,
  ArrowRight,
  Users,
  Settings,
  Sparkles,
} from "lucide-react";

export default function CRMs({ user, authToken }) {
  return (
    <>
      <SEO
        title="Email Validation for CRMs & ESPs - VALIDMAIL"
        description="Native integrations for CRM and ESP platforms. Validate at import, segment by email quality, and maintain platform reliability."
        keywords="CRM email validation, ESP integration, platform email validation, native validation"
        canonicalUrl="https://validmail.io/solutions/crms"
      />

      <div className="min-h-screen bg-white dark:bg-slate-950 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs sm:text-sm font-medium mb-4 sm:mb-6">
              <Sparkles className="h-3 w-3 sm:h-4 sm:w-4" />
              For CRMs & ESPs
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-slate-50 mb-4 sm:mb-6">
              Power your platform with{" "}
              <span className="text-blue-600 dark:text-blue-400">built-in validation</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
              Native email validation for CRM and ESP platforms. Increase platform reliability, improve user satisfaction, and ensure compliance.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-12 md:mb-16">
            <div className="bg-gradient-to-br from-blue-50 to-sky-50 dark:from-blue-900/20 dark:to-sky-900/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-blue-200 dark:border-blue-800 text-center">
              <Shield className="h-8 w-8 sm:h-10 sm:w-10 text-blue-600 dark:text-blue-400 mx-auto mb-2 sm:mb-3" />
              <div className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-50">99.9%</div>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1">Platform Uptime</p>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-emerald-200 dark:border-emerald-800 text-center">
              <Users className="h-8 w-8 sm:h-10 sm:w-10 text-emerald-600 dark:text-emerald-400 mx-auto mb-2 sm:mb-3" />
              <div className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-50">+28%</div>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1">User Satisfaction</p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-purple-200 dark:border-purple-800 text-center">
              <Activity className="h-8 w-8 sm:h-10 sm:w-10 text-purple-600 dark:text-purple-400 mx-auto mb-2 sm:mb-3" />
              <div className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-50">-71%</div>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1">Support Tickets</p>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-amber-200 dark:border-amber-800 text-center">
              <Zap className="h-8 w-8 sm:h-10 sm:w-10 text-amber-600 dark:text-amber-400 mx-auto mb-2 sm:mb-3" />
              <div className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-50">150ms</div>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1">Avg Response</p>
            </div>
          </div>

          <div className="mb-16 md:mb-24">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 dark:text-slate-50 text-center mb-8 sm:mb-12">
              How VALIDMAIL integrates with your platform
            </h2>
            <div className="relative">
              <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-blue-200 via-blue-400 to-blue-200 dark:from-blue-800 dark:via-blue-600 dark:to-blue-800 -translate-y-1/2"></div>
              
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-4 relative">
                <div className="bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-blue-200 dark:border-blue-800 shadow-lg relative">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg sm:text-xl mb-3 sm:mb-4">
                    1
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-slate-50 mb-2 text-sm sm:text-base">Import</h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                    User uploads contacts or imports from file
                  </p>
                </div>

                <div className="bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-blue-200 dark:border-blue-800 shadow-lg relative">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg sm:text-xl mb-3 sm:mb-4">
                    2
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-slate-50 mb-2 text-sm sm:text-base">Validate</h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                    Automatic validation via VALIDMAIL API
                  </p>
                </div>

                <div className="bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-blue-200 dark:border-blue-800 shadow-lg relative">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg sm:text-xl mb-3 sm:mb-4">
                    3
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-slate-50 mb-2 text-sm sm:text-base">Segment</h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                    Tag contacts with validation status and score
                  </p>
                </div>

                <div className="bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-blue-200 dark:border-blue-800 shadow-lg relative">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg sm:text-xl mb-3 sm:mb-4">
                    4
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-slate-50 mb-2 text-sm sm:text-base">Send</h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                    User sends campaigns to verified contacts
                  </p>
                </div>

                <div className="bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-blue-200 dark:border-blue-800 shadow-lg relative">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg sm:text-xl mb-3 sm:mb-4">
                    5
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-slate-50 mb-2 text-sm sm:text-base">Monitor</h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                    Track deliverability and list health over time
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-16 md:mb-24">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 dark:text-slate-50 text-center mb-8 sm:mb-12">
              Built for platform providers
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              <div className="bg-gradient-to-br from-blue-50 to-sky-50 dark:from-blue-900/20 dark:to-sky-900/20 rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-blue-200 dark:border-blue-800">
                <Plug className="h-10 w-10 sm:h-12 sm:w-12 text-blue-600 dark:text-blue-400 mb-4" />
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50 mb-3">
                  Native Integration
                </h3>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-4">
                  Seamless API integration that works at import time and on-demand. No user friction, just cleaner data.
                </p>
                <ul className="space-y-2 text-xs sm:text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">RESTful API with webhooks support</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Real-time and batch validation modes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">SDKs for all major languages</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-emerald-200 dark:border-emerald-800">
                <Database className="h-10 w-10 sm:h-12 sm:w-12 text-emerald-600 dark:text-emerald-400 mb-4" />
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50 mb-3">
                  API-Based Scoring
                </h3>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-4">
                  Enrich contact records with validation status, risk scores, and deliverability ratings for smarter segmentation.
                </p>
                <ul className="space-y-2 text-xs sm:text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Low, medium, high risk classification</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Confidence scores for catch-all domains</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Disposable and role-based detection</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-purple-200 dark:border-purple-800">
                <Shield className="h-10 w-10 sm:h-12 sm:w-12 text-purple-600 dark:text-purple-400 mb-4" />
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50 mb-3">
                  Platform Reliability
                </h3>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-4">
                  Improve your platform's reputation and reduce support burden with proactive email validation.
                </p>
                <ul className="space-y-2 text-xs sm:text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Reduce bounce-related support tickets by 71%</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Protect your email sending infrastructure</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Increase user satisfaction and retention</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-amber-200 dark:border-amber-800">
                <Settings className="h-10 w-10 sm:h-12 sm:w-12 text-amber-600 dark:text-amber-400 mb-4" />
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50 mb-3">
                  Flexible Configuration
                </h3>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-4">
                  Customize validation rules, thresholds, and workflows to match your platform's needs.
                </p>
                <ul className="space-y-2 text-xs sm:text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Custom validation policies per account</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">White-label API endpoints</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Volume-based pricing and SLAs</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 rounded-2xl sm:rounded-3xl p-8 sm:p-12 border-2 border-slate-200 dark:border-slate-700 mb-16 md:mb-24">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs sm:text-sm font-medium mb-4">
                Case Study
              </div>
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 dark:text-slate-50 mb-4">
                CRM platform reduces support tickets by 71%
              </h3>
              <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-6">
                A growing CRM platform with 50,000+ users integrated VALIDMAIL's API to automatically validate contacts at import time. Within 60 days:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-blue-600 dark:text-blue-400 mb-1">71%</div>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Fewer support tickets</p>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-blue-600 dark:text-blue-400 mb-1">28%</div>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Increase in user satisfaction</p>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-blue-600 dark:text-blue-400 mb-1">99.9%</div>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Platform uptime maintained</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-600 to-sky-600 rounded-2xl sm:rounded-3xl p-8 sm:p-12 text-center text-white shadow-2xl">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">
              Ready to integrate validation?
            </h2>
            <p className="text-base sm:text-lg md:text-xl mb-6 sm:mb-8 opacity-90 max-w-2xl mx-auto">
              Talk to our team about custom integrations and enterprise pricing.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <button
                onClick={() => window.location.href = authToken ? "/product/api" : "/signin"}
                className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-bold bg-white text-blue-600 hover:bg-slate-50 transition-all shadow-lg hover:shadow-xl inline-flex items-center justify-center gap-2"
              >
                {authToken ? "View API Docs" : "Get Started"}
                <ArrowRight className="h-5 w-5" />
              </button>
              <button
                onClick={() => window.location.href = "mailto:sales@validmail.io"}
                className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-bold border-2 border-white text-white hover:bg-white hover:text-blue-600 transition-all"
              >
                Contact Sales
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
